
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Eye } from 'lucide-react';
import { ExamQuestion, useDeleteExamQuestion } from '@/hooks/useExamQuestions';
import { EditExamQuestionDialog } from './EditExamQuestionDialog';
import { QuestionPreviewDialog } from './QuestionPreviewDialog';
import { ExamQuestionsPagination } from './ExamQuestionsPagination';

const ITEMS_PER_PAGE = 25;

interface ExamQuestionsTableProps {
  questions: ExamQuestion[];
  courseId: string;
}

export function ExamQuestionsTable({ questions, courseId }: ExamQuestionsTableProps) {
  const [editingQuestion, setEditingQuestion] = useState<ExamQuestion | null>(null);
  const [previewQuestion, setPreviewQuestion] = useState<ExamQuestion | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const deleteQuestion = useDeleteExamQuestion();

  const totalPages = Math.ceil(questions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedQuestions = questions.slice(startIndex, endIndex);

  // Reset to page 1 if current page is out of bounds (e.g., after filtering)
  React.useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this question?')) {
      deleteQuestion.mutate(id);
    }
  };

  const getCorrectAnswerText = (question: ExamQuestion) => {
    switch (question.correct_answer) {
      case 'A': return question.option_a;
      case 'B': return question.option_b;
      case 'C': return question.option_c || '';
      default: return question.correct_answer;
    }
  };

  if (questions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No exam questions found. Click "Add Question" to create your first question.
      </div>
    );
  }

  return (
    <>
      <div className="mb-3 text-sm text-muted-foreground">
        Showing {startIndex + 1}-{Math.min(endIndex, questions.length)} of {questions.length} questions
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Module</TableHead>
              <TableHead className="w-16">Part</TableHead>
              <TableHead>Question</TableHead>
              <TableHead className="w-32">Correct Answer</TableHead>
              <TableHead className="w-24">Language</TableHead>
              <TableHead className="w-20">Status</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedQuestions.map((question) => (
              <TableRow key={question.id}>
                <TableCell>{question.module_number}</TableCell>
                <TableCell>{question.part_number}</TableCell>
                <TableCell className="max-w-xs">
                  <div className="truncate" title={question.question_text}>
                    {question.question_text}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm truncate text-muted-foreground">
                    {getCorrectAnswerText(question)}
                  </div>
                </TableCell>
                <TableCell>{question.language}</TableCell>
                <TableCell>
                  <Badge variant={question.is_active ? "default" : "secondary"}>
                    {question.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewQuestion(question)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingQuestion(question)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(question.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ExamQuestionsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {editingQuestion && (
        <EditExamQuestionDialog
          question={editingQuestion}
          isOpen={!!editingQuestion}
          onClose={() => setEditingQuestion(null)}
        />
      )}

      {previewQuestion && (
        <QuestionPreviewDialog
          question={previewQuestion}
          isOpen={!!previewQuestion}
          onClose={() => setPreviewQuestion(null)}
        />
      )}
    </>
  );
}
